import { PG_CART_STATE_UUID, WL_CART_SESSION_UUID } from '@/config/cookieNames'
import {
    Circle,
    CircleText,
    CircleWrapper,
    Line,
    Message,
    Steps,
    styles,
} from '@/src/common/components/elements/Layout/StepHeaderStyledComponents'
import buildGoogleEcommerceItemsFromCart from '@/src/common/utilities/analytics/buildGoogleEcommerceItemsFromCart'
import { showCartEvent } from '@/src/common/utilities/gtag'
import { ICart } from '@/src/modules/cart/ICart'
import { getLineItemsForCart } from '@/src/modules/cart/utilities'
import { removeBoxItemFromCart } from '@/src/modules/checkout/utilities/removeBoxItemFromCart'
import { FC, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import useGetCartManually from '../../../hooks/useGetCartManually'
import usePostAddCart from '../../../hooks/usePostAddCart'
import { removeLineItemFromCart } from '../../../utilities/removeLineItemFromCart'
import CartFlyout from './CartFlyout'
import HeaderCartButton from './HeaderCartButton'
import HeaderHelpMenu from './HeaderHelpMenu'
import {
    HeaderTop,
    HeaderTopContentRight,
    HeaderWrapper,
} from '@/src/common/components/elements/Navigation/header/StyledComponents'
import HeaderLogoAndMobileNav from '@/src/common/components/elements/Navigation/header/HeaderLogoAndMobileNav'
import { TPartnerPayload } from '@/src/modules/partners/types'

interface StepInterface {
    stepNumber: number
    text: string
    currentStep: number
}

const Step = ({ stepNumber, text, currentStep }: StepInterface) => {
    const isActive = currentStep == stepNumber
    const isComplete = currentStep > stepNumber

    return (
        <CircleWrapper>
            <Circle>
                <p
                    style={
                        isActive
                            ? styles.currentStep
                            : isComplete
                            ? styles.stepComplete
                            : {}
                    }
                >
                    {!isComplete ? stepNumber : '✓'}
                </p>
            </Circle>
            <CircleText
                css={
                    isActive
                        ? styles.currentStepText
                        : isComplete
                        ? styles.completedStepText
                        : {}
                }
            >
                {text}
            </CircleText>
        </CircleWrapper>
    )
}

export interface IStepHeaderStep {
    text: string
}

export interface IStepHeader {
    steps: IStepHeaderStep[]
    currentStep: number
    message?: string
    stepsMaxWidth?: string | number
    showCart?: boolean
    showHelp?: boolean
    partnerData?: TPartnerPayload
    showMobileNav?: boolean
}

const StepHeader: FC<IStepHeader> = ({
    steps,
    currentStep,
    message,
    stepsMaxWidth,
    showCart = false,
    showHelp = false,
    partnerData,
    showMobileNav,
}) => {
    const [cartFlyoutOpen, setCartFlyoutOpen] = useState(false)
    const [cartData, setCartData] = useState<ICart>()
    const [cookies] = useCookies([PG_CART_STATE_UUID])
    const { refetch } = useGetCartManually()
    const { mutate: updateCart } = usePostAddCart()
    const [showLoader, setShowLoader] = useState(true)

    const partnerCartSessionUuid: string = cookies[WL_CART_SESSION_UUID]

    const { lineItemsInBoxes, lineItemsNotInBox } =
        getLineItemsForCart(cartData)
    const cartItemCount: number =
        lineItemsNotInBox.length + lineItemsInBoxes.length

    useEffect(() => {
        const fetchCart = () => {
            refetch().then((response) => {
                // @ts-ignore
                setCartData(response.data)
            })
        }
        fetchCart()
        setShowLoader(false)
    }, [refetch, cookies.pgCartStateUuid, cookies.whiteLabelCartStateUuid]) // Re-fetch the cart when the cart_state_uuid cookie changes

    function removeItemFromCart({
        lineItemUuid,
        boxUuid,
    }: {
        lineItemUuid?: string
        boxUuid?: string
    }) {
        setShowLoader(true)
        let newCartData = null

        if (lineItemUuid) {
            newCartData = removeLineItemFromCart(
                cartData as ICart,
                lineItemUuid
            )
        }
        if (boxUuid) {
            newCartData = removeBoxItemFromCart(cartData as ICart, boxUuid)
        }

        updateCart(
            {
                ...newCartData,
                isPartnerFlow: !!partnerData,
                hasPartnerCart: !!partnerCartSessionUuid,
            },
            {
                onSuccess: (data) => {
                    setCartData(data as ICart)
                    setShowLoader(false)
                },
                onError: (error: any) => {
                    console.error('Issue updating cart:', error)
                    setShowLoader(false)
                },
            }
        )
    }

    const fireShowCartEvent = () => {
        if (!cartData) {
            return
        }
        const formattedCartData = buildGoogleEcommerceItemsFromCart(cartData)
        showCartEvent(cartData.resolved_data.order_total, formattedCartData)
    }

    return (
        <HeaderWrapper variant={'flex'} data-testid={'stepHeader'}>
            <HeaderTop>
                <HeaderLogoAndMobileNav
                    isCorporate={false}
                    partnerData={partnerData}
                    showMobileNav={showMobileNav}
                />
                <HeaderTopContentRight>
                    {message && (
                        <Message>
                            <p>{message}</p>
                        </Message>
                    )}

                    <Steps style={{ maxWidth: stepsMaxWidth ?? '56%' }}>
                        <Line id={'header-line'} />
                        {steps.map((step, index) => (
                            <Step
                                key={step.text}
                                stepNumber={index + 1}
                                currentStep={currentStep}
                                text={step.text}
                            />
                        ))}
                    </Steps>
                    {showHelp && <HeaderHelpMenu direction={'left'} />}
                    {showCart && (
                        <>
                            <CartFlyout
                                cart={cartData}
                                isOpen={cartFlyoutOpen}
                                closeFlyout={() => {
                                    setCartFlyoutOpen(false)
                                }}
                                removeItemFromCart={removeItemFromCart}
                                showLoader={showLoader}
                                partnerData={partnerData}
                            />
                            <HeaderCartButton
                                openCartFlyout={() => {
                                    fireShowCartEvent()
                                    setCartFlyoutOpen(true)
                                }}
                                itemCount={cartItemCount}
                            />
                        </>
                    )}
                </HeaderTopContentRight>
            </HeaderTop>
        </HeaderWrapper>
    )
}

export default StepHeader
